import React from 'react';

import {
  AllItemsIcon,
  EverydayDealsIcon,
  EndingSoonIcon,
  BogoIcon,
  BundlesIcon,
} from 'src/assets/specials-filter-icons';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { useHideSalesLanguage } from 'src/hooks/use-hide-sales-language';
import useUI from 'src/hooks/use-ui';
import { specialsMatchFilterKey, SpecialFilterKey } from 'src/hooks/use-special-filter-param';
import { SpecialMenuCard, MenuFilterChipsSettings } from 'src/gql/graphql';

import { ChipsGrid, ChipLink, ChipLabel, ChipIcon } from './styles';

type MenuFilterChipFlag = 'showAllItems' | 'showBogo' | 'showBundles' | 'showEndingSoon' | 'showEverydayDeals';

type ChipDefinition = {
  key: SpecialFilterKey;
  label: string;
  complianceLabel: string;
  icon: React.ComponentType;
  settingsFlag: MenuFilterChipFlag;
};

const CHIP_DEFINITIONS: ChipDefinition[] = [
  {
    key: 'all',
    label: 'All Items On Special',
    complianceLabel: 'All Items On Offer',
    icon: AllItemsIcon,
    settingsFlag: 'showAllItems',
  },
  {
    key: 'everyday',
    label: 'Everyday Deals',
    complianceLabel: 'Everyday Deals',
    icon: EverydayDealsIcon,
    settingsFlag: 'showEverydayDeals',
  },
  {
    key: 'ending-soon',
    label: 'Ending Soon',
    complianceLabel: 'Ending Soon',
    icon: EndingSoonIcon,
    settingsFlag: 'showEndingSoon',
  },
  { key: 'bogo', label: 'BOGO', complianceLabel: 'BOGO', icon: BogoIcon, settingsFlag: 'showBogo' },
  { key: 'bundles', label: 'Bundles', complianceLabel: 'Bundles', icon: BundlesIcon, settingsFlag: 'showBundles' },
];

type SpecialsFilterChipsProps = {
  specials: SpecialMenuCard[];
  menuFilterChips?: MenuFilterChipsSettings | null;
};

export function SpecialsFilterChips({ specials, menuFilterChips }: SpecialsFilterChipsProps): JSX.Element | null {
  const { dispensary } = useDispensary();
  const { dispensaryRootUrl } = useUI();
  const hideSalesLanguage = useHideSalesLanguage();

  const visibleChips = CHIP_DEFINITIONS.filter(
    (chip) => menuFilterChips?.[chip.settingsFlag] !== false && specialsMatchFilterKey(chip.key, specials)
  );

  if (!dispensary || visibleChips.length === 0) {
    return null;
  }

  const { cName } = dispensary;

  const ariaLabel = hideSalesLanguage ? 'Offer filters' : 'Special filters';

  return (
    <ChipsGrid aria-label={ariaLabel} data-testid='specials-filter-chips'>
      {visibleChips.map((chip) => {
        const label = hideSalesLanguage ? chip.complianceLabel : chip.label;
        const href = `/${dispensaryRootUrl}/${cName}/products?specialFilter=${chip.key}`;

        return (
          <ChipLink key={chip.key} href={href}>
            <ChipIcon aria-hidden='true'>
              <chip.icon />
            </ChipIcon>
            <ChipLabel>{label}</ChipLabel>
          </ChipLink>
        );
      })}
    </ChipsGrid>
  );
}
